import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { STATUS, DOC_TYPE_LABEL, DOC_TYPE_CLS, fmtDate } from "@/lib/helpers";
import { User, ChevronRight, ClipboardCheck, CalendarDays, Hand, Undo2 } from "lucide-react";

export default function MyPickups() {
  const nav = useNavigate();
  const [docs, setDocs] = useState(null);

  const load = () => api.get("/requests/mine").then((r) => setDocs(r.data)).catch(() => setDocs([]));
  useEffect(() => { load(); }, []);
  const release = async (e, rid) => { e.stopPropagation(); try { await api.post(`/requests/${rid}/release`); toast("Réservation libérée — le document redevient disponible"); load(); } catch (_) { toast.error("Erreur"); } };

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-heading font-extrabold text-brand-900">Mes documents à récupérer</h1>
      {docs === null && <p className="text-slate-400 py-10 text-center">Chargement…</p>}
      {docs && docs.length === 0 && (
        <div className="text-center py-16 text-slate-400">
          <ClipboardCheck className="w-12 h-12 mx-auto mb-3 text-slate-300" />
          <p className="font-semibold">Vous n'avez pris aucune récupération en charge.</p>
        </div>
      )}
      <div className="space-y-3">
        {docs?.map((d) => {
          const st = STATUS[d.display_status] || STATUS.a_recuperer;
          const clickable = d.display_status !== "recupere";
          return (
            <div key={d.id} data-testid={`mine-${d.id}`} role="button" tabIndex={0}
              onClick={() => clickable && nav(`/driver/retour/${d.id}`)}
              className={`w-full bg-white rounded-2xl border border-slate-200/70 p-5 shadow-sm text-left active:scale-[0.98] transition-transform ${clickable ? "" : "opacity-70"}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 flex-wrap">
                  {d.reservation === "mine" ? <span data-testid={`mine-reserved-${d.id}`} className="inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full border bg-green-50 text-green-700 border-green-200"><Hand className="w-3.5 h-3.5" />{d.reserved_label}</span>
                    : <span className={`text-xs font-bold px-2.5 py-1 rounded-full border ${st.cls}`}>{st.label}</span>}
                  <span data-testid={`mine-type-${d.id}`} className={`text-xs font-bold px-2.5 py-1 rounded-full ${DOC_TYPE_CLS[d.document_type] || DOC_TYPE_CLS.autre}`}>{DOC_TYPE_LABEL[d.document_type] || d.document_type_label}</span>
                </div>
                {clickable && <ChevronRight className="w-5 h-5 text-slate-300" />}
              </div>
              <p className="text-lg font-heading font-bold text-brand-900 mt-2 flex items-center gap-2"><User className="w-5 h-5 text-slate-400" />{d.patient_name}</p>
              <p className="text-sm text-slate-500 mt-0.5">{d.establishment_name}{d.service ? ` · ${d.service}` : ""}</p>
              <p data-testid={`mine-dob-${d.id}`} className="text-xs text-slate-400 mt-0.5">{d.patient_birth_date ? `Né(e) le ${fmtDate(d.patient_birth_date)}` : "Date de naissance non renseignée"}</p>
              {d.transport_date && <p data-testid={`mine-transport-${d.id}`} className="text-sm text-brand-900 font-semibold mt-1 flex items-center gap-1.5"><CalendarDays className="w-4 h-4 text-slate-400" />Transport du {fmtDate(d.transport_date)}{d.transport_time ? ` à ${d.transport_time}` : ""}</p>}
              {d.instruction && <p data-testid={`mine-instruction-${d.id}`} className="mt-2 text-sm bg-orange-50 border border-orange-200 text-orange-900 rounded-lg px-3 py-2 font-semibold">{d.instruction}</p>}
              {d.reservation === "mine" && <button data-testid={`mine-release-${d.id}`} onClick={(e) => release(e, d.id)} className="mt-3 h-10 px-4 rounded-xl border-2 border-slate-200 text-slate-600 text-sm font-bold inline-flex items-center gap-1.5 active:scale-[0.98]"><Undo2 className="w-4 h-4" />Libérer</button>}
            </div>
          );
        })}
      </div>
    </div>
  );
}
